import type { AdminGame } from '@mcgames/contracts';
import { Button, Dialog } from '@mcgames/ui';
import { useTranslation } from 'react-i18next';
import { ErrorText } from './ErrorText.tsx';
import { useAddReleaseVersion, useLatestImport } from './import-hooks.ts';
import { IndexerSearch } from '../indexer/IndexerSearch.tsx';
import styles from './Games.module.css';

export type FindNzbDialogProps = {
  game: AdminGame;
  onClose: () => void;
};

/** Searches the indexer and imports the picked release as a new version of the game. */
export function FindNzbDialog({ game, onClose }: FindNzbDialogProps) {
  const { t } = useTranslation('admin');
  const add = useAddReleaseVersion(game.id);
  const latest = useLatestImport(game.id);
  const running = latest.data?.status === 'running';

  return (
    <Dialog
      open
      size="wide"
      onClose={onClose}
      title={t('games.versions.findNzb')}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('actions.close', { ns: 'common' })}
        </Button>
      }
    >
      <p className={styles['hint']}>{t('games.versions.findHint')}</p>
      <IndexerSearch
        initialQuery={game.title}
        actionLabel={t('games.nzb.useRelease')}
        actionAriaLabel={(release) => t('games.nzb.useReleaseNamed', { title: release.title })}
        pendingGuid={add.isPending ? (add.variables?.guid ?? null) : null}
        isBusy={() => running}
        onPick={(release) =>
          add.mutate({ guid: release.guid, title: release.title }, { onSuccess: onClose })
        }
      />
      <ErrorText error={add.error} />
    </Dialog>
  );
}
